/**
 * 이상한 방 - 매치(여럿이) 상태 모델의 타입 전부
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(라운드 구조) · 4.3절(탄환 경제 · 관전) · 4.4절(원래 방 정원 1) · 4.5절(점수)
 *            `이상한방-상태모델-v0.1.md` 9절(CPU) · 11.2절(클라이언트가 가진 것) · 11.3절(서버가 쥐는 것) · 12절(동시 클릭)
 *            로드맵 모델 2.2절(이 파일은 그 절의 사본이 아니라 원본이다)
 *
 * ## `gameState.ts`를 감싸지도 확장하지도 않는다 - 병행한다
 *
 * 술래마다 `GameState`를 두면 `original/mutated/changes`가 N벌이고 `found`가 개인 소유라 선착순이
 * 표현되지 않는다. `GameState`를 확장하면 96개 명세와 `sessionView`·`reveal`이 한꺼번에 흔들리고,
 * 튜토리얼은 서버 없이 돌아야 한다. 그래서 매치는 **자기 상태·이벤트·효과**를 갖고, 판정·탄환 값·점수·
 * 콤보·탄창 산술·원래 방 타이머·리빌 길이·변화 생성은 싱글과 **같은 리프 함수**를 부른다.
 * 그 리프들이 좁은 인자(`ShotJudgeState`·`ScoreState`·`Pick<GameState, …>`·`OriginVisit`)를 받게
 * 다듬어진 것이 M0이고, `MatchPlayer`의 필드 이름이 `GameState`의 것과 같은 이유가 그것이다 -
 * 좌석을 **그대로** 넘긴다. 어댑터가 하나라도 생기면 그 어댑터가 두 번째 규칙 사본이 된다.
 *
 * ## `PLAYER_EVENT_TYPES`의 원본은 여기다
 *
 * `net/protocol.ts`가 이 상수로 "클라이언트가 보낼 수 있는 것"을 검사한다. 반대 방향(프로토콜이 정의하고
 * 리듀서가 가져다 쓰는 것)이면 규칙 층이 전송 층을 import 하게 되어 화살표가 뒤집힌다.
 *
 * ## 아직 자리만 있는 필드
 *
 * `predictions`·`unfairVotes`·`revealIndex`(M8 관전자 참여)는 **타입에 자리만 있고 리듀서가 기본값으로 채운다.**
 * 나중에 필드를 추가하면 그때까지 쌓인 스냅샷·픽스처가 전부 깨지므로 모양을 먼저 굳힌다.
 * 규칙은 그 마일스톤이 붙인다. `raised`·`wager`(M7 걸기)는 M7이 규칙을 붙였다.
 *
 * **`placementsLeft`는 자리를 비웠다**(M10). 꾸미기의 잔여 배치 수는 방에서 세면 나오는 값이고
 * (`core/room.countByPlayer` - `core/verbs.applyAdd`가 한도를 집행할 때 보는 바로 그 함수다), 좌석에도 적어 두면
 * 두 값이 갈리는 날이 온다. 화면이 읽는 것은 뷰의 `self.placementsLeft`이고 그것도 같은 함수에서 나온다.
 *
 * ## 역할이 없다 - 구획 배정이 그 자리다(M11)
 *
 * `Role`·`changerId`·`firstChanger`·`changerPool`은 M11에서 걷었다. 한 라운드에 한 명이 고치고 나머지가 기다리던
 * 구조가 **전원이 자기 구획을 고치고 남의 구획을 찾는** 구조로 바뀌었기 때문이다. 누가 어디인지는 상태에 없고
 * `matchZones.assignZones(seed, round, order, plan)`이 그때그때 만든다 - 같은 수를 두 곳에 적으면 갈릴 자리가 생긴다.
 *
 * 이 파일은 `core`·`catalog`·`game` 리프만 본다(`boundary.test.ts`가 소스에서 검사한다) - 서버가 그대로 import 한다.
 */

import type { FloorPlan } from '../../core/floorPlan'
import type { Rng } from '../../core/rng'
import type { Catalog } from '../../core/room'
import type { WireObject } from '../../core/roomCodec'
import type { GenerateOptions } from '../../core/roomGen'
import type {
  Cell3,
  InstanceId,
  ObjectInstance,
  PlayerId,
  RoomConfig,
  RoomState,
  Verb,
} from '../../core/types'
import type { VerbRejection, VerbRequest } from '../../core/verbs'
import type { Change } from '../changeGen'
import type { GenerateChangesFn, SoundId } from '../gameState'
import type { HitTarget, ShotJudgeState, ShotOutcome } from '../hunt'
import type { OriginVisit } from '../originVisit'
import type { Tuning } from '../tuning'

/* --- 기본 어휘 ------------------------------------------------------------------ */

/**
 * 매치 페이즈(컨셉시트 3절). 싱글의 넷(`gameState.Phase`)에 `lobby`·`decorate`·`change`가 붙었다.
 *
 * **도는 순서는 이 유니온의 순서가 아니다** - `decorate` → `change` → `watch` → `hunt`다(3절 개정: 외우기는
 * 바꾸기 **뒤**다. 외운 기억을 1분 딴짓하며 썩히지 않기 위해서다 - `matchPhases.openChange` 머리말).
 * 유니온의 순서를 바꾸지 않는 이유는 이것이 **값의 집합**이지 흐름이 아니기 때문이다. 흐름은 `matchPhases.ts` 하나에 있다.
 *
 * `decorate`는 `config.humanDecorate`가 참일 때만(M10), `change`는 **손댈 사람이 하나라도 있을 때만** 거친다 -
 * 전원이 이웃이거나 전원 끊긴 라운드는 라운드 시작에서 곧장 외우기로 간다(`matchPhases.ts` 머리말).
 */
export type MatchPhase = 'lobby' | 'decorate' | 'watch' | 'change' | 'hunt' | 'reveal' | 'result'

export type SeatKind = 'human' | 'cpu'

/**
 * 수색이 끝난 이유. 싱글의 `out-of-ammo`가 `all-out`으로 바뀐 것이 첫 번째 차이다 -
 * 여럿이서는 한 사람의 잔탄 0이 끝이 아니라 관전 전환이고(4.3절), **술래 전원**이 관전이어야 끝난다.
 *
 * `stalled`가 두 번째 차이이고 여럿이에만 있다. 제 구획의 이상한 곳은 주인이 못 찾으므로(M11 규칙 4)
 * **남은 것이 전부 마지막 술래의 제 구획**인 순간이 생기는데, 그건 전부 찾은 것도 전원 탄환 소진도 아니면서
 * 아무도 아무것도 할 수 없는 상태다(`matchPhases.settleHunt`). 이유를 `all-found`로 뭉치지 않는 이유는
 * 결과 화면이 사실을 말해야 하기 때문이다 - 두 개가 남았는데 "전부 찾았다"는 거짓이다.
 */
export type MatchEndReason = 'all-found' | 'all-out' | 'stalled' | 'timeout' | 'skipped'

/** 상태모델 9.1절의 (N, p, q) + 원래 방 방문 확률. 시드에서 파생되고 라운드 내내 고정이다. */
export type BotProfile = Readonly<{
  intervalMs: number
  findChance: number
  missChance: number
  originChance: number
}>

/* --- 좌석 ------------------------------------------------------------------------ */

export type MatchPlayer = Readonly<{
  id: PlayerId
  name: string
  kind: SeatKind
  /** CPU 좌석의 행동 프로필. 사람은 `null`. */
  bot: BotProfile | null
  /** 표시용. **규칙은 이 값을 보지 않는다** - 끊긴 좌석은 그대로 남아 라운드를 끝까지 간다(11.3절). */
  connected: boolean
  ready: boolean
  /**
   * 술래의 라운드 자원. **`GameState`의 같은 이름 필드와 뜻이 같다**(`matchParity.test.ts`가 지킨다).
   * 이름이 같아야 `gameState.magazineUsesLeft`·`hasRoomForFullMagazine`·`hunt.computeScoreBreakdown`에
   * 좌석을 그대로 넘길 수 있다.
   */
  ammo: number
  magazines: number
  magazinesUsed: number
  combo: number
  maxCombo: number
  hasPerfect: boolean
  /** 잔탄과 탄창이 함께 바닥나 관전으로 넘어갔는가(4.3절). 다음 라운드 외우기 진입에서 자동 복귀한다. */
  spectating: boolean
  spectateTarget: PlayerId | null
  /** 개인별 체류·쿨타임. 정원 1은 `door`(공유 토큰)의 문제이고 이 셋은 사람마다 따로 흐른다(4.4절). */
  origin: OriginVisit
  /**
   * 걸기(M7)를 선언했는가. `GameState.raised`와 뜻이 같다(선택 필드가 아닌 것만 다르다 - 좌석은 이 필드와 함께 태어났다).
   * 선언 뒤 발견은 ×2, 빗나감은 `raiseMissCost`발(`hunt.ts`·`raise.ts`). 라운드마다 `freshSeat`가 되돌린다.
   */
  raised: boolean
  /**
   * 이 라운드에 내가 건 것(M7 "내 것 N개는 아무도 못 찾는다"). 커밋 순간 굳고(`matchChange.onCommitChanges`)
   * 수색 진입에서 실제 개수로 한 번 더 접힌다(`matchPhases.enterHunt`). 결과 진입까지 살아 있어야 점수가 읽는다.
   * 작업대가 아니라 좌석에 있는 이유는 작업대가 수색 진입에서 사라지기 때문이다.
   */
  wager: ChangerWager | null
  roundScore: number
  totalScore: number
}>

/**
 * 이 라운드의 이상한 곳 한 건 + **주인**(로드맵 M11). `changeGen.Change` 그대로에 `owner` 하나가 붙었다.
 *
 * 주인은 "누구 구획에서 난 것인가"이고 두 규칙의 입력이다 - 그 사람은 이것을 **못 쏘고**(`matchShoot.onShoot`),
 * 아무도 못 찾으면 그 사람의 점수가 된다(`matchScore.zoneRoundScore`). 채움(생성기)이 만든 것도 주인이 있다 -
 * 예산을 남기고 끝낸 사람의 구획에서 난 것이므로 그 사람 몫이다(M11 규칙 8).
 *
 * 별도 필드로 싣는 이유는 배정표를 다시 묻지 않기 위해서다. `zoneId`로도 유도할 수 있지만, 그러면 점수·사격 게이트가
 * 매번 배정표(라운드마다 회전한다)를 다시 만들어 대조해야 하고 그 대조가 곧 두 번째 사본이다.
 */
export type MatchChange = Change & Readonly<{ owner: PlayerId }>

/** 공유 발견 목록의 한 줄. `by`·`owner`가 있는 것이 `gameState.FoundEntry`와의 차이다. */
export type MatchFound = Readonly<{
  changeId: string
  by: PlayerId
  /** 찾힌 이상한 곳의 주인(`MatchChange.owner`). "내가 찾을 수 있는 것"의 분모·분자를 가르는 값이다. */
  owner: PlayerId
  /** 수색 페이즈 시작 기준 경과(ms). 속도 보너스와 `sniped` 간격의 입력이다. */
  foundAtMs: number
  /** 이 발견이 성립시킨 콤보 수. 배수는 맞힌 순간의 값으로 굳는다(`gameState.FoundEntry.combo`와 같은 이유). */
  combo: number
  /** 걸기(M7) 선언 뒤의 발견인가. `gameState.FoundEntry.raised`와 같다 - 없으면 걸지 않은 것. */
  raised?: boolean
}>

/* --- 원래 방 문 ------------------------------------------------------------------ */

/**
 * 컨셉시트 4.4 정원 1. 서버는 **토큰만** 관리한다(11.3절) - 카메라·워프는 클라이언트의 일이다.
 * **자동 워프는 없다.** 차례가 오면 `reservedFor`에 이름이 오르고 유예가 흐른다 - 누르지 않으면 넘어간다.
 * 사람이 보고 있던 곳에서 갑자기 원래 방으로 끌려가는 것은 관찰을 끊는 가장 나쁜 방식이다.
 */
export type OriginDoor = Readonly<{
  holder: PlayerId | null
  /** 입장 대기 줄. 머리(`queue[0]`)가 `reservedFor`가 된다. */
  queue: readonly PlayerId[]
  reservedFor: PlayerId | null
  /** 예약 유예 잔여(ms). `reservedFor`가 없으면 0. */
  reservedRemainingMs: number
}>

/* --- 바꾸기 작업대 --------------------------------------------------------------- */

/**
 * M7. "내 것 N개는 아무도 못 찾는다" 걸기. 맞으면 그 몫이 ×2, 틀리면 배수만 없다(`raise.wagerMultiplier`).
 * M11에서 한 명이 아니라 **좌석마다** 하나다(`MatchPlayer.wager`) - 전원이 자기 구획을 걸 수 있다.
 * `raise.WagerLike`와 같은 모양이다 - 리프가 매치 타입을 import 하지 않으려고 이름만 둘이다.
 */
export type ChangerWager = Readonly<{ unfoundAtLeast: number }>

/**
 * 바꾸기 작업대. **좌석마다 하나**다(M11 - 전원이 동시에 자기 구획을 고친다).
 *
 * `draft`는 원본 + **그 사람의 구획 안에서 그 사람이 한 것**뿐이다. 남에게는 어떤 경로로도 가지 않는다
 * (`matchView.roomsFor`가 자기 것만 열고, 호스트의 `rooms`도 받는 사람의 작업대에서 꺼낸다) - 남의 초안이 새면
 * 수색이 시작되기 전에 정답표의 절반을 손에 쥐게 된다. 남에게 가는 것은 개수(`PublicPlayer.changeUsed`)뿐이다.
 */
export type ChangeDesk = Readonly<{
  /** 이 사람의 몫(`tuning.changesPerPlayer`). 라운드 전체는 `좌석 수 × 이 값`이다. */
  budget: number
  /** 5.1절 한 물건 한 번. 마감 채움 때 생성기가 이 목록을 `excludeIds`로 받는다. */
  touched: readonly InstanceId[]
  draft: RoomState
  /** 이 사람이 만든 것. 주인은 전부 자기다 - 자기 구획 밖에는 손댈 수 없으므로(`core/verbs.allowedRect`). */
  changes: readonly MatchChange[]
  /** [다 했다]를 눌렀는가. 전원이 누르면 바꾸기가 그 자리에서 끝난다(`matchDesk.everyoneCommitted`). */
  committed: boolean
}>

/**
 * 체인저가 요청하는 변화 한 건. `core/verbs.VerbRequest` **그것이다**(M4에서 그쪽으로 내렸다 - 사람 체인저의 도구
 * `input/changerTool.ts`가 같은 요청을 만들어야 하는데 `input/`은 `game/`을 보지 않는다). 여기서 다시 export 하는 이유는
 * 이 파일이 이벤트 어휘의 원본이라 리듀서·프로토콜·테스트가 여기서 가져가기 때문이다. 이름을 바꾸거나 감싸면 변환 코드가
 * 생기고 그 변환이 두 번째 규칙 사본이 된다.
 */
export type { VerbRequest }

/* --- 설정 · 상태 ----------------------------------------------------------------- */

export type MatchConfig = Readonly<{
  /** allowedVerbs · changeCount · placementsPerPlayer(상태모델 5.3절). 튜닝과 갈리지 않게 `createMatch`가 튜닝에서 채운다. */
  room: RoomConfig
  /**
   * 수색부터 동사별 개수("생김 2, 없어짐 3, …")를 전원에게 보여 주는가. **기본 켬**(M4) - 단계 안내 화면이 싱글에서
   * 이미 같은 어휘로 예고한다. 무엇이 어디인지는 어차피 없고, M11부터 개수는 **보는 사람이 찾을 수 있는 것**만이라
   * 사람마다 다르다(`matchView.verbCountsFor` - 내 구획의 동사를 함께 읊으면 남의 몫이 복원된다).
   */
  revealVerbCounts: boolean
  /**
   * 참이면 판이 **꾸미기 페이즈**로 시작한다(M10) - 빈 셸에 전원이 인당 `room.placementsPerPlayer`개를 놓고,
   * 그 방이 이 매치의 방이 된다. 거짓이면 `roomGen` 프리셋으로 방을 세운다.
   *
   * 방장이 켜도 **놓을 손이 없으면** 그 페이즈는 서지 않고(`matchPhases.needsDecorate`), 첫 라운드에만 선다 -
   * 방은 매치 내내 하나다(9.5절).
   */
  humanDecorate: boolean
}>

/**
 * `configure`의 몸. `room`이 `Partial`인 이유는 로비(M6)가 "종류만" 또는 "개수만" 바꾸기 때문이다 - 방장이 개수 하나를 올리는데 허용 동사와
 * 인당 배치 수(뷰에 없다)까지 다시 적어 보내야 하면 로비가 방 규칙 전체를 알아야 한다. 리듀서(`matchState.onConfigure`)는 처음부터
 * `room`을 얕게 덮어 왔고 와이어 모양(`net/protocolShapes.isConfigPatchShape`)도 부분을 허용했다 - 타입만 뒤늦게 그 사실을 따라왔다.
 */
export type MatchConfigPatch = Readonly<Partial<Omit<MatchConfig, 'room'>> & { room?: Partial<RoomConfig> | undefined }>

export type MatchState = Readonly<{
  roomCode: string
  /**
   * 방 시드. 방은 매치 내내 고정이고 라운드 변화 시드는 `${seed}:r${round}`다 -
   * `gameState.ts`의 시드 분리 규약(방 = seed / 변화 = 파생)을 그대로 따른다.
   */
  seed: string
  /** 로비는 0. `start`가 1로 올리고 `nextRound`가 하나씩 올린다. */
  round: number
  phase: MatchPhase
  /** 마지막 `tick`의 `atMs`. 다음 `tick`의 `dtMs = atMs - clockMs`이고 병합 불변의 기준이다. */
  clockMs: number
  phaseElapsedMs: number
  config: MatchConfig
  hostId: PlayerId
  /** 입장 순서 = 구획 배정의 회전 방향(`matchZones.assignZones`). 좌석이 나가면 여기서도 빠진다(로비에서만). */
  order: readonly PlayerId[]
  players: Readonly<Record<PlayerId, MatchPlayer>>
  original: RoomState
  /** 외우기 동안은 `original`과 같은 참조다. */
  mutated: RoomState
  /** **서버 전용.** `toClientView`가 수색 중에는 벗긴다(리빌부터 준다). 각 줄에 주인이 있다(`MatchChange.owner`). */
  changes: readonly MatchChange[]
  found: readonly MatchFound[]
  door: OriginDoor
  /**
   * 바꾸기 페이즈에만 있다. **좌석마다 하나**이고 키는 좌석 id다(M11 - 전원이 동시에 고친다).
   * 페이즈 밖에서는 통째로 `null`이다 - 빈 표를 두면 "바꾸기 중인가"를 두 곳에서 물을 수 있게 된다.
   */
  desks: Readonly<Record<PlayerId, ChangeDesk>> | null
  /** M8 관전자 점찍기. 술래에게 안 간다. 지금은 항상 빈 객체. */
  predictions: Readonly<Record<PlayerId, readonly Cell3[]>>
  /** M8 "이건 못 봐" 투표. 점수 무관. 지금은 항상 빈 객체. */
  unfairVotes: Readonly<Record<string, readonly PlayerId[]>>
  /** M8 리빌 스텝 인덱스. 호스트가 쥔다. 지금은 항상 0. */
  revealIndex: number
  /**
   * 지금 페이즈를 넘기자고 표를 던진 사람들(`matchPhases.onSkipVote`). **그 페이즈의 것**이라
   * 페이즈가 바뀌면 `reduceMatch`가 한 자리에서 지운다.
   *
   * 집합이 아니라 배열인 이유는 이 상태가 통째로 직렬화되기 때문이다(`net/protocol`) - `Set`은 JSON을 건너지 못한다.
   */
  skipVotes: readonly PlayerId[]
  endReason: MatchEndReason | null
}>

/* --- 이벤트 ---------------------------------------------------------------------- */

/**
 * 클라이언트가 보낼 수 있는 것. **여기가 원본**이고 `net/protocol.ts`가 가져다 쓴다.
 * 와이어에는 `by`가 없고 호스트가 신원으로 찍는다 - 클라이언트가 자기 이름을 적어 보내면 그게 곧 위장이다.
 */
export const PLAYER_EVENT_TYPES = [
  'ready', 'configure', 'start', 'nextRound', 'skipPhase',
  'place', 'unplace',
  'applyChange', 'undoChange', 'commitChanges',
  'shoot', 'useMagazine', 'raise',
  'requestOrigin', 'leaveOrigin', 'leaveQueue',
  'spectate', 'predict', 'voteUnfair',
] as const

export type PlayerEventType = (typeof PLAYER_EVENT_TYPES)[number]

export type PlayerEvent =
  | Readonly<{ type: 'ready'; ready: boolean }>
  /** 방장만. 로비에서만. `room`도 부분 패치다 - 리듀서가 얕게 덮는다(`matchState.onConfigure`). */
  | Readonly<{ type: 'configure'; patch: MatchConfigPatch }>
  /** 방장만. 전원 ready여야 한다. */
  | Readonly<{ type: 'start' }>
  /** 방장만. 결과에서만. */
  | Readonly<{ type: 'nextRound' }>
  /** 방장만. */
  | Readonly<{ type: 'skipPhase' }>
  /** M10. `placedBy`는 호스트가 찍는다. */
  | Readonly<{ type: 'place'; obj: Omit<ObjectInstance, 'placedBy'> }>
  | Readonly<{ type: 'unplace'; id: InstanceId }>
  /** 체인저만(M4). */
  | Readonly<{ type: 'applyChange'; request: VerbRequest }>
  | Readonly<{ type: 'undoChange'; id: InstanceId }>
  | Readonly<{ type: 'commitChanges'; wager: ChangerWager | null }>
  | Readonly<{ type: 'shoot'; hit: HitTarget | null }>
  | Readonly<{ type: 'useMagazine' }>
  /** M7. */
  | Readonly<{ type: 'raise' }>
  /** 입장 · 줄서기 · 예약 수락이 전부 이것 하나다 - 문 앞에서 사람이 하는 일은 "들어가겠다" 하나뿐이다. */
  | Readonly<{ type: 'requestOrigin' }>
  | Readonly<{ type: 'leaveOrigin' }>
  | Readonly<{ type: 'leaveQueue' }>
  | Readonly<{ type: 'spectate'; target: PlayerId | null }>
  /** M8 관전자. */
  | Readonly<{ type: 'predict'; cell: Cell3 }>
  /** M8. */
  | Readonly<{ type: 'voteUnfair'; changeId: string; unfair: boolean }>

/**
 * 유니온과 상수가 갈리면 컴파일이 멈춘다. 이벤트를 하나 추가하고 상수를 빠뜨리면 프로토콜이 그 이벤트를
 * `not-player-event`로 거절하는데, 그 실패는 화면에서 "버튼이 안 먹는다"로만 보인다.
 */
type Equals<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false
type AssertTrue<T extends true> = T
export type PlayerEventTypesMatch = AssertTrue<Equals<PlayerEvent['type'], PlayerEventType>>

/** 호스트만 넣는 것. 클라이언트가 보내면 `protocol`이 `not-player-event`로 거절한다. */
export type HostEvent =
  /** 절대 시각. 리듀서가 `dtMs = atMs - clockMs`를 뽑아 dt 기반 리프를 돈다. */
  | Readonly<{ type: 'tick'; atMs: number }>
  | Readonly<{
      type: 'join'
      player: Pick<MatchPlayer, 'id' | 'name' | 'kind' | 'bot'>
      /** 계정이 들고 온 탄창 수. `progress.ts`에서 오고, 리듀서는 저장소를 읽지 않으므로 여기로 들어온다. */
      magazines: number
    }>
  | Readonly<{ type: 'leave'; playerId: PlayerId }>
  | Readonly<{ type: 'reconnect'; playerId: PlayerId }>

export type JoinEvent = Extract<HostEvent, { type: 'join' }>

export type MatchEvent = HostEvent | (PlayerEvent & Readonly<{ by: PlayerId }>)

/* --- 효과 ------------------------------------------------------------------------ */

/**
 * 여럿이에서만 나는 소리. 싱글 `SoundId`와 합치지 않는 이유는 `audio/sounds.ts`의 표가 싱글 것이라서다 -
 * 여기 이름을 더하면 그 표(다른 층)가 이 파일을 따라와야 한다. 어떻게 울릴지는 M2가 정한다.
 */
export type PartySoundId = 'other-found' | 'sniped' | 'spectate-enter' | 'ranking'

/** 효과는 수신자를 갖는다. `'all'`은 방 전체, 아니면 그 사람에게만 - 호스트가 걸러 보낸다. */
export type MatchEffect = Readonly<{ to: 'all' | PlayerId; body: MatchEffectBody }>

export type MatchEffectBody =
  | Readonly<{ type: 'phaseEnter'; phase: MatchPhase; previous: MatchPhase; round: number }>
  /** 호스트가 `rooms` 메시지를 보낼 신호. 페이즈 경계에서만 난다. */
  | Readonly<{ type: 'roomsChanged' }>
  /** 수신자 개인. */
  | Readonly<{ type: 'warpTo'; origin: boolean }>
  | Readonly<{ type: 'syncProxies' }>
  | Readonly<{ type: 'playSound'; id: SoundId | PartySoundId }>
  /**
   * 수신자 개인. 클라이언트가 판정하지 않는 근거 - 루프백에서도 이것을 기다린다.
   * `own-zone`은 M11이 더한 것이다 - **내 구획의 이상한 곳**을 쐈다. 탄환도 콤보도 그대로이므로
   * `already-found`와 같은 취급이고(로드맵 M11 규칙 4), 판정 종류(`hunt.ShotOutcome`)가 아니라 여기에만 있다 -
   * 싱글에는 구획 주인이 없어 `hunt.ts`에 넣으면 싱글이 쓰지 않는 갈래가 판정기에 생긴다(패리티).
   */
  | Readonly<{ type: 'shotResolved'; kind: ShotOutcome['kind'] | 'blocked' | 'own-zone'; targetId: InstanceId | null }>
  /**
   * 받는 사람마다 하나씩(M11). `remaining`이 **그 사람이 아직 찾을 수 있는 수**라 값이 사람마다 다르다 -
   * 내 구획의 이상한 곳은 내가 못 찾으므로 "N개 남았다"의 N이 전원 공통일 수 없다.
   */
  | Readonly<{ type: 'foundChange'; by: PlayerId; changeId: string; targetId: InstanceId; remaining: number }>
  /** 넘어가기 표가 움직였다. 화면이 "몇 명이 눌렀나"를 세는 자리다(`matchPhases.onSkipVote`). */
  | Readonly<{ type: 'skipVotes'; votes: number; needed: number }>
  /** 늦은 쪽에게만. 손해 0 - 상태모델 12절의 동시 클릭을 피드백으로 바꾼 것이다. */
  | Readonly<{ type: 'sniped'; by: PlayerId; gapMs: number }>
  | Readonly<{ type: 'comboUp'; combo: number; multiplier: number }>
  | Readonly<{ type: 'comboBroken'; at: number }>
  | Readonly<{ type: 'lowAmmo'; ammo: number }>
  | Readonly<{ type: 'magazineUsed'; ammo: number; remaining: number }>
  /** all. */
  | Readonly<{ type: 'spectating'; playerId: PlayerId; target: PlayerId | null }>
  | Readonly<{
      type: 'originEntryDenied'
      reason: 'cooldown' | 'occupied'
      remainingMs: number
      /** 줄에 섰으면 1부터 세는 내 자리. 줄이 아니면 `null`. */
      queuePosition: number | null
    }>
  /**
   * 수신자 개인. 원래 방 재입장 쿨타임이 틱에서 0에 닿았다. 상태 변화만 있고 효과가 없으면 호스트가 `step`을 내지
   * 않으므로(`matchHost.commit` - 시계만 흐른 틱은 조용하다), 이 신호가 없으면 클라이언트의 버튼은 다음 무관한 `step`이
   * 올 때까지 쿨타임 얼굴로 잠긴다. 싱글에는 없는 효과지만 상태 열은 같다(패리티는 상태를 비교한다).
   */
  | Readonly<{ type: 'originReady' }>
  /** 줄 머리에게만. */
  | Readonly<{ type: 'doorYourTurn'; graceMs: number }>
  /** all. 줄서기 연출("빨리 나와!!")의 재료다. */
  | Readonly<{ type: 'doorChanged'; door: OriginDoor; holderRemainingMs: number }>
  /** 체인저에게만(M4). */
  | Readonly<{ type: 'changeAccepted'; verb: Verb; budgetLeft: number }>
  /** 체인저에게만(M4). `editor.reject.*` 키와 같은 문자열. */
  | Readonly<{ type: 'changeRejected'; reason: VerbRejection }>
  /**
   * all. 무엇을 건드렸는지는 없다 - **누가 몇 개**까지다(M11 규칙 3 - 남의 진행은 예산 점 개수만).
   * `by`가 붙은 이유는 전원이 동시에 만들기 때문이다 - 없으면 옆 사람의 수용이 내 도구의 대기 요청을 확정한다.
   */
  | Readonly<{ type: 'changerProgress'; by: PlayerId; used: number; budget: number }>
  /**
   * all(M10 꾸미기). **한 물건만** 실린다 - 방 전체를 다시 보내면 60초 동안 그 짓을 수십 번 하게 되고,
   * `roomsChanged`는 페이즈 경계의 신호다(`matchChange.ts` 머리말). 실린 것은 `WireObject`라 `placedBy`가 없고
   * 누가 놓았는지는 `by`로만 간다(2.1절 - 그 값이 방 데이터에 섞이면 방이 곧 배치자 지도가 된다).
   */
  | Readonly<{ type: 'placed'; by: PlayerId; obj: WireObject }>
  /** all(M10). 걷은 사람은 언제나 놓은 사람이다(`matchDecorate.onUnplace`). */
  | Readonly<{ type: 'unplaced'; by: PlayerId; id: InstanceId }>
  /** 놓으려던 사람에게만(M10). 사유는 `core/verbs`의 것 그대로다 - `changeRejected`와 같은 규약이다. */
  | Readonly<{ type: 'placeRejected'; reason: VerbRejection }>
  /** all 배너(M7). */
  | Readonly<{ type: 'raised'; by: PlayerId }>
  /** M8/M10. 문장은 클라이언트가 조립한다. */
  | Readonly<{
      type: 'revealStep'
      index: number
      changeId: string
      foundBy: PlayerId | null
      placerId: PlayerId | null
    }>
  /** M8. */
  | Readonly<{ type: 'predictionScored'; by: PlayerId; hits: number }>
  | Readonly<{ type: 'roundScored'; ranking: readonly RankingRow[] }>
  /** 수신자 개인. 핀은 호스트가 계산한다(`economy/`). */
  | Readonly<{ type: 'roundRewards'; pins: number }>

/** 순위표 한 줄. `rank`는 이번 라운드 순위이고 같은 점수는 같은 순위다. */
export type RankingRow = Readonly<{
  playerId: PlayerId
  roundScore: number
  totalScore: number
  rank: number
}>

/** 리듀서 결과. 상태는 항상 새 객체이거나 입력과 **동일 참조**(무시된 이벤트)다. */
export type MatchStep = Readonly<{ state: MatchState; effects: readonly MatchEffect[] }>

/* --- 의존성 ---------------------------------------------------------------------- */

/**
 * `gameState.GenerateChangesFn` **그 타입이다**(별칭). 옵션(`excludeIds` - 사람 체인저의 잔여 예산 채움)도 거기 붙어 있다.
 * 별도 타입을 두면 싱글 쪽에 옵션이 안 붙은 채 구조적으로만 호환되는 상태가 눈에 안 띈다.
 */
export type MatchGenerateChangesFn = GenerateChangesFn

/** `hunt.resolveShot`의 모양. 좁은 인자를 받으므로 `MatchState`의 조각을 그대로 넘긴다. */
export type MatchResolveShotFn = (state: ShotJudgeState, target: HitTarget | null) => ShotOutcome

/**
 * CPU 한 좌석의 판단. 호스트가 `cpu.cpuDecisionsDue`가 세는 만큼 `kind === 'cpu'`인 자리마다 부르고, 결과를
 * **사람 이벤트와 같은 큐**에 넣는다(9.3절 "새 코드 경로가 생기지 않는다"). 리듀서는 이 함수를 부르지 않는다 - 누가 넣었는지 모른다.
 */
export type CpuDecideFn = (state: MatchState, playerId: PlayerId, rng: Rng) => readonly PlayerEvent[]

/**
 * 리듀서가 필요로 하는 바깥 세계 전부. 준비물(kit)이 없는 것이 **"뽑기가 사람 매치를 바꾸지 않는다"의 타입 증명**이다.
 */
export type MatchDeps = Readonly<{
  tuning: Tuning
  catalog: Catalog
  /**
   * 좌석 수 → 평면도(로드맵 M11). **값이 아니라 함수인 것이 규칙이다** - 여럿이 판의 구획 수는 좌석 수와
   * 정확히 같아야 하고(남는 구획은 아무도 손대지 않은 방, 모자라는 구획은 고칠 곳이 없는 사람이다),
   * 방 하나가 프로세스 하나의 `deps`를 여럿이 나눠 쓰므로(`server/src/gateway.ts`) 고정 평면도로는 방마다
   * 다른 인원을 담을 수 없다.
   *
   * 좌석 수는 **판이 시작되는 순간 굳는다** - 합류는 로비에서만 일어나고 진행 중 퇴장은 좌석을 남기므로
   * (`matchState.onLeave`), 로비를 벗어난 뒤의 `seatsInOrder(state).length`는 라운드가 바뀌어도 변하지 않는다.
   * 그래서 상태에 좌석 수를 따로 굳혀 두지 않고 `matchSelectors.matchPlan`이 그때그때 묻는다 -
   * 같은 수를 두 곳에 적으면 둘이 갈릴 자리가 생긴다.
   */
  planFor: (seatCount: number) => FloorPlan
  roomOptions?: GenerateOptions
  generateChanges: MatchGenerateChangesFn
  resolveShot: MatchResolveShotFn
  /**
   * **리듀서는 부르지 않는다.** 봇 판단은 호스트(M2 `net/matchHost.ts`)가 스케줄하고 결과를 이벤트로 넣는다. 그래도
   * 여기 사는 이유는 로드맵 모델 2.2절이 "리듀서가 필요로 하는 바깥 세계"에 봇을 함께 둔 것과, 호스트·서버가 `MatchDeps`
   * 하나로 매치를 세우기 때문이다. `matchState`·`matchPhases`·`matchShoot`·`matchOrigin`에 `cpuDecide`가 보이면 그건 봇이
   * 리듀서 안에 새 경로를 낸 것이다(9.3절 위반).
   */
  cpuDecide: CpuDecideFn
}>
